import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpUtils } from 'app/shared/http-utils';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, map } from 'rxjs/operators';
import { DEDICATED_FLEET_URL_BASE } from 'app/core/constants';
import { SortDirection } from 'app/shared/sort-direction';
import { SearchObjectResult } from 'app/shared/search-object-result.model';
import { DFCountryCarrierList } from './df-management.coutry.model';
import { MasterData } from './df-master-data.model';
import { DFStatus, DFStatusCount, MyDFStatusCountList, TransactionData } from './df-transaction-data.model';
import { DFAdmin, DFFlexibleCalendar } from './df-management-admin-data.modal';
import { DFMonthlyDataRequest, TimeZone } from './df-admin-country.model';
import { FileService } from 'app/shared/services/file.service';
import { DFUploadMasterData } from './file-upload-master-data.model';
import { DFUploadTransactionData } from './file-upload-transaction-data.model';
import { LogsData } from './df-management-logs.modal';
import { NotificationAlerts } from './df-management-alerts.model';

@Injectable({
              providedIn: 'root'
            })
export class DfManagementService {

  private MasterData_URI = 'fleetmanagement';
  private scheduler_URL = 'api/scheduler/onboardCountry';

  constructor(private http: HttpClient, private fileService: FileService) {
  }

  public getAllcountry(): Observable<SearchObjectResult<DFCountryCarrierList>> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/getAllCountry`;
    return this.http.get(url, { headers })
               .pipe(map((result: DFCountryCarrierList[]) => SearchObjectResult.fromDto(result,
                                                                                        DFCountryCarrierList.fromDto)));
  }

  public getAllCarrierIdBasedOnCountry(countryCode: string): Observable<DFCountryCarrierList[]> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/getDedicatedFleetAsset/${countryCode}/`;
    return this.http.get(url, { headers })
               .pipe(map((result: DFCountryCarrierList[]) => {
                 return result;
               }));
  }

  public search(config, assets): Observable<SearchObjectResult<MasterData>> {
    const page = config?.page;
    const pageSize = config?.pageSize;
    const sortColumn = config?.sortColumn?.name;
    const direction = config?.direction;
    const filters = config?.filters;
    const countryCode = config?.countryCode;
    const params = this.prepareSearchRequest(page, pageSize, sortColumn, direction, filters);
    const headers = HttpUtils.createHeaders();
    if (assets === 'fleetManagement') {
      const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/assetCharge/search/${countryCode}`;
      return this.http.get(url, { params, headers })
                 .pipe(map((result: MasterData[]) => SearchObjectResult.fromDto(result, MasterData.fromDto)));
    } else {
      const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/liveAssetCharge/search/${countryCode}`;
      return this.http.get(url, { params, headers })
                 .pipe(map((result: MasterData[]) => SearchObjectResult.fromDto(result, MasterData.fromDto)));
    }
  }

  public searchAllTransactionLoads(config, loads: string): Observable<SearchObjectResult<TransactionData>> {
    const page = config?.page;
    const pageSize = config?.pageSize;
    const sortColumn = config?.sortColumn?.name;
    const direction = config?.direction;
    const filters = config?.filters;
    const countryCode = config?.countryCode;
    const headers = HttpUtils.createHeaders();
    const params = this.prepareSearchRequest(page, pageSize, sortColumn, direction, filters);
    if (loads === 'fleetManagement') {
      const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/loadingCharge/search/${countryCode}`;
      return this.http.get(url, { params, headers })
                 .pipe(map((result: TransactionData[]) => SearchObjectResult.fromDto(result, TransactionData.fromDto)));
    } else {
      const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/liveLoadingCharge/search/${countryCode}`;
      return this.http.get(url, { params, headers })
                 .pipe(map((result: TransactionData[]) => SearchObjectResult.fromDto(result, TransactionData.fromDto)));
    }
  }

  public deleteByAssetChargeId(id: string) {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/deleteAssetCharge/${id}`;
    return this.http.delete(url, { headers })
               .pipe(map(r => MasterData.fromDto(r)));
  }

  public addAssetCharge(masterData: any): Observable<MasterData> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/addAssetCharge`;
    return this.http.post(url, masterData, { headers })
               .pipe(map((result: any) => {
                 return result;
               }));
  }

  public downloadAllMasterData(countryCode: string) {
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/upload-contracts/template/${countryCode}`;
    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    return this.http.get(url, { headers, responseType: 'blob' });
  }

  public uploadAllMasterData(file: File, countryCode: string): Observable<DFUploadMasterData> {
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/upload-contracts/${countryCode}`;
    const headers = HttpUtils.createHeaders();

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(url, formData, { headers })
               .pipe(map(result => DFUploadMasterData.fromDto(result)));
  }

  public downloadFailureTransactionData(countryCode: string) {
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/failed-loads/download/${countryCode}`;
    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    return this.http.get(url, { headers, responseType: 'blob' });
  }

  public downloadTransactionDataUploadTemplate(countryCode: string) {
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/upload-transaction-charges/template/${countryCode}`;
    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    return this.http.get(url, { headers, responseType: 'blob' });
  }

  public downloadAllTransactions(countryCode: string, statusCode?: any, filters?: any) {
    let url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/loadingCharge/search/download/${countryCode}`;
    const params = new URLSearchParams();

    if (statusCode && statusCode.id) {
      params.append('statusCode', statusCode.id);
    }
    if (filters) {
      if (filters.carrierCodeList) {
        params.append('carrierCodeList', filters.carrierCodeList);
      }
      if (filters.assetId) {
        params.append('assetId', filters.assetId);
      }
      if (filters.loadIds) {
        params.append('loadIds', filters.loadIds.join(','));
      }
      if (filters.entryStartDate) {
        params.append('entryStartDate', filters.entryStartDate);
      }
      if (filters.entryEndDate) {
        params.append('entryEndDate', filters.entryEndDate);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'application/octet-stream');

    return this.http.get(url, { headers, responseType: 'blob' });
  }

  public uploadAllTransactionData(file: File,
                                  countryCode: string,
                                  shippedStartDate: string,
                                  shippedEndDate: string,
                                  carrierCodeList: string): Observable<DFUploadTransactionData> {
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/upload-transaction-charges/${countryCode}`;
    const headers = HttpUtils.createHeaders();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('shippedStartDate', shippedStartDate);
    formData.append('shippedEndDate', shippedEndDate);
    formData.append('carrierCodeList', carrierCodeList);

    return this.http.post(url, formData, { headers })
               .pipe(map(result => DFUploadTransactionData.fromDto(result)));
  }

  public getStatusCount(config): Observable<DFStatusCount> {
    const page = config?.page;
    const pageSize = config?.pageSize;
    const sortColumn = config?.sortColumn?.name;
    const direction = config?.direction;
    const filters = config?.filters;
    const countryCode = config?.countryCode;
    const headers = HttpUtils.createHeaders();
    const params = this.prepareSearchRequest(page, pageSize, sortColumn, direction, filters);
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/loadingCharge/statusCount/${countryCode}`;
    return this.http.get(url, { params, headers })
               .pipe(map((result: DFStatusCount) => {
                 return result;
               }));
  }

  public getAllStatus(): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/getLoadingStatus`;
    return this.http.get(url, { headers })
               .pipe(map((result: any[]) => result['results'].map(r => DFStatus.fromDto(r))));
  }

  public addLoadingCharge(transactionData: TransactionData): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/addLoadingCharge`;
    return this.http.post(url, TransactionData.toDto(transactionData), { headers });
  }

  public addOnBordingCountry(onBordingCountryData: any, sequencial: boolean): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.scheduler_URL}?sequential=${sequencial}`;
    return this.http.post(url, onBordingCountryData, { headers })
               .pipe(map((result: any) => {
                 return result;
               }));
  }

  public getAllTimeZones(): Observable<TimeZone[]> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/admin/timezone`;
    return this.http.get(url, { headers })
               .pipe(map((result: TimeZone[]) => {
                 return result;
               }));
  }

  public getAllCountry(): Observable<DFAdmin[]> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/admin/country`;
    return this.http.get(url, { headers })
               .pipe(map((result: DFAdmin[]) => {
                 return result;
               }));
  }

  public getVariableChargesByCountry(): Observable<DFAdmin[]> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/admin/charges-by-country`;
    return this.http.get(url, { headers })
               .pipe(map((result: DFAdmin[]) => {
                 return result;
               }));
  }

  public addVariableChargesByCountry(variableChargesData: any): Observable<DFAdmin> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/admin/add-variable-charges-by-country`;
    return this.http.post(url, variableChargesData, { headers }).pipe(map((result: any) => {
      return result;
    }));
  }

  public updateBulkUploadCharges(countryCode: string,
                                 carrierCode: string,
                                 assetId: string,
                                 loadIds: string,
                                 charges: any): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/bulkUpdateCharges/${countryCode}?carrierCode=${carrierCode}&assetId=${assetId}&loadIds=${loadIds}`;
    return this.http.put(url, charges, { headers }).pipe(map((result: any) => {
      return result;
    }));
  }

  public bulkBtfSubmit(countryCode: string, loadIds: Array<string>): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/submit-btf`;
    return this.http.post(url, { country_code: countryCode, load_ids: loadIds}, { headers });
  }

  public checkDuplicateVariableCharges(variableChargesData: any): Observable<DFAdmin> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/admin/duplicate-variable-charges`;
    return this.http.post(url, variableChargesData, { headers }).pipe(map((result: any) => {
      return result;
    }));
  }

  public getLogsBasedOnDates(config, countryCode: string): Observable<SearchObjectResult<LogsData>> {
    const page = config?.page;
    const pageSize = config?.pageSize;
    const sortColumn = config?.sortColumn?.name;
    const direction = config?.direction;
    const filters = config?.filters;
    const headers = HttpUtils.createHeaders();
    const params = this.prepareSearchFields(page, pageSize, sortColumn, direction, filters);
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/scheduler/logs/${countryCode}`;
    return this.http.get(url, { params, headers })
               .pipe(map((result: LogsData[]) => SearchObjectResult.fromDto(result, LogsData.fromDto)));
  }

  public getStatusCountList(config): Observable<MyDFStatusCountList> {
    const page = config?.page;
    const pageSize = config?.pageSize;
    const sortColumn = config?.sortColumn?.name;
    const direction = config?.direction;
    const filters = config?.filters;
    const countryCode = config?.countryCode;
    const headers = HttpUtils.createHeaders();
    const params = this.prepareSearchFields(page, pageSize, sortColumn, direction, filters);
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/scheduler/logs/count/${countryCode}`;
    return this.http.get(url, { params, headers })
               .pipe(map((result: MyDFStatusCountList) => {
                 return result;
               }));
  }

  public getNotificationsAlerts(countryCode: string): Observable<NotificationAlerts[]> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/voucher-update/notifications/${countryCode}`;
    return this.http.get(url, { headers })
               .pipe(map((result: NotificationAlerts[]) => {
                 return result;
               }));
  }

  public addFlexibleCalendarData(countryCode: string, body: DFMonthlyDataRequest[]): Observable<DFFlexibleCalendar> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/calendar/upload/${countryCode}`;
    return this.http.post(url, body, { headers })
               .pipe(map((result: any) => {
                 return result;
               }));
  }

  public getFlexibleCalendarData(countryCode: string, selectedYear: number): Observable<DFMonthlyDataRequest[]> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/calendar/fetch/${countryCode}?year=${selectedYear}`;
    return this.http.get(url, { headers })
               .pipe(map((result: DFMonthlyDataRequest[]) => result), catchError((error: any) => {
                 console.error('Error fetching calendar data:', error);
                 throw error;
               }));
  }

  public updateSupressCharges(countryCode: string, loadIds: string): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/suppressCharges/${countryCode}?loadIds=${loadIds}`;
    return this.http.put(url, loadIds, { headers }).pipe(map((result: any) => {
      return result;
    }));
  }

  public uploadTransactionToBTF(countryCode: string, transactionDataIds: string[]): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${DEDICATED_FLEET_URL_BASE}/${this.MasterData_URI}/submit-btf`;
    return this.http.post(url, {country_code: countryCode, load_ids: transactionDataIds}, { headers }).pipe(map(() => {
      return;
    }));
  }

  private prepareSearchRequest(page: number, size: number, sortColumn: string, direction: SortDirection, filters: any) {

    filters = this.prepareFilters(filters);
    const params = {
      page: String(page), size: String(size), direction: direction, sort: sortColumn, ...filters
    };
    return params;
  }

  private prepareFilters(filters: any) {
    if (!filters.carrierCode) {
      delete filters['carrierCode'];
    }
    if (!filters.loadId) {
      delete filters['loadId'];
    }

    if (!filters.search) {
      delete filters['search'];
    }

    if (!filters.assetId) {
      delete filters['assetId'];
    }

    if (!filters.statusCode) {
      delete filters['statusCode'];
    }
    if (!filters.entryEndDate) {
      delete filters['entryEndDate'];
    }
    if (!filters.entryStartDate) {
      delete filters['entryStartDate'];
    }

    if (!filters.carrierCodeList) {
      delete filters['carrierCodeList'];
    }

    return filters;
  }

  private prepareSearchFields(page: number, size: number, sortColumn: string, direction: SortDirection, filters: any) {

    filters = this.prepareFilter(filters);
    const params = {
      page: String(page), size: String(size), direction: direction, sort: sortColumn, ...filters
    };
    return params;
  }

  private prepareFilter(filters: any) {
    if (!filters.jobNames) {
      delete filters['jobNames'];
    }
    if (!filters.jobStatus) {
      delete filters['jobStatus'];
    }
    if (!filters.jobStartDate) {
      delete filters['jobStartDate'];
    }
    if (!filters.jobEndDate) {
      delete filters['jobEndDate'];
    }

    return filters;
  }
}
